import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  Security as SecurityIcon,
  LockOpen as LockOpenIcon,
  Lock as LockIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import TwoFactorSetup from './TwoFactorSetup';
import axios from 'axios';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  twoFactorEnabled: boolean;
}

const SecuritySettings: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSetup2FA, setShowSetup2FA] = useState(false);
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [token, setToken] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setUser(response.data.user);
      } catch (err: any) {
        setError('Failed to load user profile');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const handleSetup2FA = () => {
    setShowSetup2FA(true);
  };

  const handleSetupComplete = () => {
    setShowSetup2FA(false);
    // Refresh user data to show updated 2FA status
    fetchUserProfile();
  };

  const handleDisable2FA = () => {
    setShowDisable2FA(true);
  };

  const handleDisableCancel = () => {
    setShowDisable2FA(false);
    setToken('');
  };

  const handleDisableConfirm = async () => {
    if (!token) return;

    setDisableLoading(true);
    try {
      await axios.post('/api/auth/2fa/disable', { token }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setShowDisable2FA(false);
      setToken('');
      
      // Refresh user data
      fetchUserProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disable 2FA');
    } finally {
      setDisableLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setUser(response.data.user);
    } catch (err) {
      console.error('Failed to refresh user profile', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (showSetup2FA) {
    return <TwoFactorSetup onComplete={handleSetupComplete} />;
  }

  return (
    <Paper elevation={3} sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SecurityIcon sx={{ fontSize: 28, mr: 2, color: 'primary.main' }} />
        <Typography variant="h5">Security Settings</Typography>
      </Box>
      
      <Divider sx={{ mb: 4 }} />
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Two-Factor Authentication (2FA)
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Two-factor authentication adds an extra layer of security to your account by requiring a verification code in addition to your password.
        </Typography>
        
        <Box sx={{ 
          p: 2, 
          bgcolor: user?.twoFactorEnabled ? 'success.light' : 'warning.light',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          mb: 3
        }}>
          {user?.twoFactorEnabled ? (
            <>
              <LockIcon sx={{ color: 'success.dark', mr: 2 }} />
              <Typography variant="body1" sx={{ color: 'success.dark', fontWeight: 500 }}>
                Two-factor authentication is enabled
              </Typography>
            </>
          ) : (
            <>
              <LockOpenIcon sx={{ color: 'warning.dark', mr: 2 }} />
              <Typography variant="body1" sx={{ color: 'warning.dark', fontWeight: 500 }}>
                Two-factor authentication is not enabled
              </Typography>
            </>
          )}
        </Box>
        
        {user?.twoFactorEnabled ? (
          <Button 
            variant="outlined" 
            color="error" 
            onClick={handleDisable2FA}
            startIcon={<LockOpenIcon />}
          >
            Disable Two-Factor Authentication
          </Button>
        ) : (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSetup2FA}
            startIcon={<LockIcon />}
          >
            Enable Two-Factor Authentication
          </Button>
        )}
      </Box>
      
      <Divider sx={{ my: 4 }} />
      
      <Box>
        <Typography variant="h6" gutterBottom>
          Password
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          It's recommended to use a strong, unique password and change it periodically.
        </Typography>
        
        <Button 
          variant="outlined"
          color="primary"
        >
          Change Password
        </Button>
      </Box>
      
      {/* Dialog to confirm disabling 2FA */}
      <Dialog
        open={showDisable2FA}
        onClose={handleDisableCancel}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon sx={{ color: 'warning.main', mr: 1 }} />
          Disable Two-Factor Authentication
        </DialogTitle>
        <DialogContent>
          <DialogContentText paragraph>
            Disabling two-factor authentication will make your account less secure. Are you sure you want to continue?
          </DialogContentText>
          <DialogContentText paragraph>
            Please enter the verification code from your authenticator app to confirm:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Verification Code"
            type="text"
            fullWidth
            variant="outlined"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDisableCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleDisableConfirm} 
            color="error"
            disabled={disableLoading || token.length < 6}
          >
            {disableLoading ? <CircularProgress size={24} /> : 'Disable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SecuritySettings; 